import React, { useEffect, useMemo, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { CircularProgress, Typography } from '@mui/material'

import { BusinessTypeForm } from '../../settings/BusinessType'
import Setup from './setup'
import WizardFooter from '../shared/WizardFooter'
import WizardContainer from '../shared/WizardContainer'
import useDropdowns from '../../hooks/api/useDropdowns'
import useBusinessTypes from '../../hooks/api/useBusinessTypes'
import useAlert from '../../hooks/useAlert'

const CustomerProfileSetup = () => {
  const {
    navigateToNext,
    companyConfig,
    setCompanyConfig,
    updateCompanyConfig,
    wizardState,
    updateWizardState,
  } = useOutletContext()

  const [step, setStep] = useState(0)
  const [setupTab, setSetupTab] = useState(0)

  const { dropdownsSummary = [] } = wizardState

  const hasInitializedData = useMemo(() => !!wizardState.dropdownsSummary, [])

  const {
    businessTypes,
    isLoading: btIsLoading,
    hasLoadError: btHasLoadError,
  } = useBusinessTypes()

  const {
    dropdownsSummary: dropdownsSummaryData,
    setDropdownsSummary: setDropdownsSummaryData,
    isLoading: ddIsLoading,
    hasLoadError: ddHasLoadError,
    saveDropdowns,
  } = useDropdowns()

  const setDropdownsSummary = (dropdownsSummary) => {
    setDropdownsSummaryData(dropdownsSummary)
    updateWizardState({ dropdownsSummary })
  }

  useEffect(() => {
    if (hasInitializedData) return setDropdownsSummaryData(dropdownsSummary)

    updateWizardState({ dropdownsSummary: dropdownsSummaryData })
  }, [dropdownsSummaryData])

  const { createAlert } = useAlert()

  const isLoading = btIsLoading || ddIsLoading || !companyConfig
  const hasLoadError = btHasLoadError || ddHasLoadError

  useEffect(() => {
    if (hasLoadError) navigateToNext()
  }, [hasLoadError])

  const goToNextStep = () => setStep((step) => step + 1)
  const goToPreviousStep = () => setStep((step) => step - 1)

  const submit = () => {
    return saveDropdowns()
      .then(([, res]) => {
        navigateToNext(false, res.data.companyConfig)
      })
      .catch(() => {
        createAlert({
          severity: 'error',
          message: 'Something went wrong',
          action: {
            label: 'Skip this step',
            onClick: () => navigateToNext(),
          },
        })
      })
  }

  const saveCompanyConfig = () =>
    updateCompanyConfig()
      .then(goToNextStep)
      .catch((e) => {
        createAlert({
          severity: 'error',
          message: 'Something went wrong',
          action: {
            label: 'Skip this step',
            onClick: () => navigateToNext(),
          },
        })
      })

  const updateDropdown = (options, i) => {
    const newArr = [...dropdownsSummary]
    newArr[i] = { ...newArr[i], options }
    setDropdownsSummary(newArr)
  }

  const hasRequiredDropdownWithNoOptions = dropdownsSummary.some(
    (dd) => dd.isRequired && dd.options.length === 0
  )

  const steps = useMemo(() => {
    const isOnLastTab = setupTab === dropdownsSummary.length - 1
    const isOnFirstTab = setupTab === 0

    return [
      {
        hideSkipButton: true,
        primaryButton: {
          onClick: saveCompanyConfig,
        },
      },
      {
        hideSkipButton: true,
        primaryButton: {
          onClick: isOnLastTab ? submit : () => setSetupTab((t) => t + 1),
          label: isOnLastTab ? 'Save and continue' : 'Next',
          disabled: hasRequiredDropdownWithNoOptions,
        },
        backButton: {
          onClick: isOnFirstTab
            ? goToPreviousStep
            : () => setSetupTab((t) => t - 1),
        },
      },
    ]
  }, [
    dropdownsSummary,
    setupTab,
    companyConfig,
    hasRequiredDropdownWithNoOptions,
  ])

  const { primaryButton, hideSkipButton, skipButton, backButton } = steps[step]

  if (isLoading) return <CircularProgress />

  return (
    <WizardContainer>
      <Typography variant={'h5'}>Account Profile Dropdowns</Typography>

      {step === 0 && (
        <BusinessTypeForm
          businessTypes={businessTypes}
          companyConfig={companyConfig}
          setCompanyConfig={setCompanyConfig}
          isInWizard
        />
      )}

      {step === 1 && dropdownsSummary && dropdownsSummary.length > 0 && (
        <Setup
          tab={setupTab}
          setTab={setSetupTab}
          updateDropdown={updateDropdown}
          dropdownSummary={dropdownsSummary}
        />
      )}

      <WizardFooter
        hideSkipButton={hideSkipButton}
        skipButton={skipButton}
        primaryButton={primaryButton}
        backButton={backButton}
      />
    </WizardContainer>
  )
}

export default CustomerProfileSetup
